import  { type Genre } from "@ca.vmedia.tv/domains";
import { GenreCardData } from "@/components/Cards/types";
import { Services } from "@ca.vmedia.tv/services";
import type { AxiosError } from 'axios';
import { Logger } from '@/utils';
import type { ExposeInterface } from './types';

export const useGenresStore = defineStore('Genres', (): ExposeInterface => {
    const _topGenres: Ref<Genre[] | null> = ref(null);
    const _fetchGenresPromise: Ref<Promise<void> | null> = ref(null);
    const getTopGenres: ComputedRef<Genre[] | null> = computed(() => {
        if (_topGenres.value === null) {
            return null;
        }
        
        if (_topGenres.value?.length === 0) {
            return [];
        }

        return _topGenres.value.filter((genre) => genre.id !== null);
    });

    const getTotalCount: ComputedRef<number> = computed(() => {
        return _topGenres.value?.length ?? 0;
    });

    const getAllGenres: ComputedRef<GenreCardData[]> = computed(() => {
        if ((_topGenres.value?.length ?? 0) === 0 || _topGenres.value === null) {
            return [];
        }
        return _topGenres.value
            .filter((genre) => genre.id !== null)
            .map(
                (genre) =>
                    new GenreCardData(genre)
            );
    });

    const _subGenres: Ref<Genre[] | null> = ref([]);
    const getSubGenres = (id: number): Genre[] => {
        const genresCount = _subGenres.value?.length ?? 0;
        if (genresCount == 0) {
            return [];
        }

        const subGenrers = _subGenres.value?.filter((genre: Genre) => genre.parentId === id);
        if (!subGenrers || subGenrers.length === 0) {
            return [];
        }
        return subGenrers.sort(
            (genre1, genre2) => {
                if (genre1.orderNumber === genre2.orderNumber) {
                    return 0;
                }
                if (genre1.orderNumber === null) {
                    return -1;
                }
                if (genre2.orderNumber === null) {
                    return 1;
                }
                return genre1.orderNumber > genre2.orderNumber ? 1 : -1;
            }
        );
    };
    const getSubChannels = (id: number): GenreCardData[] => {
        const genresCount = _subGenres.value?.length ?? 0;

        if (genresCount == 0) {
            return [];
        }

        const subGenrers = _subGenres.value
            ?.filter((genre: Genre) => genre.parentId === id && (genre.childrenIds !=null && genre.childrenIds.length > 0))
            .map(
                (genre) =>
                    new GenreCardData(genre)
            );

        if (!subGenrers || subGenrers.length === 0) {
            return [];
        }

        return subGenrers;
    };

    const getGenre = (id: number): Genre | null => {
        const genresCount = _topGenres.value?.length ?? 0;

        if (genresCount === 0 || _topGenres.value === null) {
            return null;
        }

        const genresSelected = _topGenres.value.filter((genre) => genre.id === id);
        if (genresSelected.length > 0) {
            return genresSelected[0];
        }

        const subgenres = _subGenres.value?.filter((childGenre) => childGenre.id === id);
        if (subgenres && subgenres.length > 0) {
            return subgenres[0];
        }
        return null;
    };

    const _setGenres = (genres: Map<Genre, ReadonlyArray<Genre>>): void => {
        _topGenres.value = [...genres.keys()].filter(genre => genre.name != "remainder");
        _subGenres.value = [];
        for (const [_key, value] of genres) {
            _subGenres.value.push(...value);
        }
    };

    const clearAllGenres = (): void => {
        _topGenres.value = null;
        _subGenres.value = [];
        _fetchGenresPromise.value = null;
    };

    const setGenreContent = (updatedGenre: Genre): void => {
        _topGenres.value
            ?.filter((genre: Genre) => genre.id === updatedGenre.id)
            ?.forEach((genre: Genre) => genre.content = updatedGenre.content);
        _subGenres.value
            ?.filter((genre: Genre) => genre.id === updatedGenre.id)
            ?.forEach((genre: Genre) => genre.content = updatedGenre.content);
    };

    const _fetchGenres = async (hasCrave: boolean): Promise<void> => {
        try {
            const genresMap = await Services.Instance.vod.getSVODGenres(hasCrave);
            _setGenres(genresMap);
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _fetchGenresPromise.value = null;
        }
    };

    const fetchGenres = async (hasCrave: boolean): Promise<void> => {
        if (_fetchGenresPromise.value !== null) {
            return _fetchGenresPromise.value;
        }

        if (_topGenres.value !== null) {
            return;
        }

        _fetchGenresPromise.value = _fetchGenres(hasCrave);
        return _fetchGenresPromise.value;
    };

    return {
        getTopGenres,
        getTotalCount,
        getAllGenres,
        getSubGenres,
        getGenre,
        getSubChannels,
        clearAllGenres,
        setGenreContent,
        fetchGenres
    };
});